import React from 'react';

import { LocaleContext } from '@/hooks/useTranslator';
import { Locale } from '@/types/locale';
import { numbersLocale } from '@/utility/numbersLocale';

export interface Separators {
  readonly decimalSeparator: string;
  readonly groupSeparator: string;
}

export const useLocalizedNumericSeparators = (): Separators => {
  const locale = React.useContext<Locale>(LocaleContext);

  const formatter = React.useMemo(
    (): Intl.NumberFormat =>
      new Intl.NumberFormat(numbersLocale(locale), {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        useGrouping: true,
      }),
    [locale],
  );

  const groupSeparator = React.useMemo((): string => {
    return (
      formatter
        .formatToParts(1000)
        .find((part: Intl.NumberFormatPart): boolean => part.type === 'group')?.value ?? ''
    );
  }, [formatter]);

  const decimalSeparator = React.useMemo((): string => {
    return (
      formatter
        .formatToParts(1.1)
        .find((part: Intl.NumberFormatPart): boolean => part.type === 'decimal')?.value ?? ''
    );
  }, [formatter]);

  return { decimalSeparator, groupSeparator };
};
